import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Paper,
  Rating,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { authFetch } from "../../Utils/apiClient";

// Ratings people give a live stream when they leave it (1–5 stars and an
// optional comment), so the team can see how the service is received.

const MAX_PAGES = 20;

const formatDate = (value) =>
  new Date(value).toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

const StreamRatings = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [type, setType] = useState("all");
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const all = [];
      let first = null;
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const query = `page=${page}${type === "all" ? "" : `&type=${type}`}`;
        const res = await authFetch(`/livestream/ratings?${query}`);
        const json = await res.json();
        if (!res.ok || !json.status) throw new Error(json.message || "Could not load ratings");
        first = first || json;
        all.push(...json.ratings);
        if (all.length >= json.total || json.ratings.length < json.pageSize) break;
      }
      setSummary(first?.summary || null);
      setRows(all);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = [
    { field: "createdAt", headerName: "Date", width: 170, renderCell: (p) => formatDate(p.value) },
    { field: "stars", headerName: "Rating", width: 150, renderCell: (p) => <Rating value={p.value} readOnly size="small" /> },
    { field: "comment", headerName: "Comment", flex: 1, minWidth: 240, renderCell: (p) => p.value || "—" },
    { field: "topic", headerName: "Live stream", width: 200, renderCell: (p) => p.value || p.row.channelname },
    {
      field: "streamType",
      headerName: "Type",
      width: 100,
      renderCell: (p) => <Chip size="small" label={p.value === "private" ? "Private" : "Public"} color={p.value === "private" ? "secondary" : "default"} />,
    },
    {
      field: "role",
      headerName: "Who",
      width: 100,
      renderCell: (p) => (p.value === "host" ? "Host" : p.value === "speaker" ? "Speaker" : "Viewer"),
    },
    { field: "name", headerName: "Name", width: 160, renderCell: (p) => p.value || (p.row.signedIn ? "Member" : "Guest") },
  ];

  const total = summary?.count || 0;

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb="20px">
        <Header title="Live Stream Ratings" subtitle="What people say about the live stream service when they leave a stream" />
        <ToggleButtonGroup size="small" exclusive value={type} onChange={(_, v) => v && setType(v)}>
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="public">Public</ToggleButton>
          <ToggleButton value="private">Private</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "260px 1fr" }} gap={2} mb={2}>
        <Paper sx={{ p: 2.5, textAlign: "center" }}>
          <Typography variant="h1" fontWeight={700}>
            {summary?.average != null ? summary.average.toFixed(1) : "—"}
          </Typography>
          <Rating value={summary?.average || 0} precision={0.1} readOnly />
          <Typography variant="body2" color="text.secondary">
            {total} {total === 1 ? "rating" : "ratings"}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2.5 }}>
          {[5, 4, 3, 2, 1].map((n) => {
            const count = summary?.distribution?.[n] || 0;
            const pct = total ? Math.round((count / total) * 100) : 0;
            return (
              <Box key={n} display="flex" alignItems="center" gap={1.5} mb={0.75}>
                <Typography variant="body2" sx={{ width: 50 }}>
                  {n} star{n === 1 ? "" : "s"}
                </Typography>
                <LinearProgress variant="determinate" value={pct} sx={{ flex: 1, height: 10, borderRadius: 5 }} color={n >= 4 ? "success" : n === 3 ? "warning" : "error"} />
                <Typography variant="body2" sx={{ width: 70, textAlign: "right" }}>
                  {count} ({pct}%)
                </Typography>
              </Box>
            );
          })}
        </Paper>
      </Box>

      <Box
        height="60vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none", fontSize: "14px" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.greenAccent[700], borderBottom: "none" },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": { borderTop: "none", backgroundColor: colors.greenAccent[700] },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          components={{ Toolbar: GridToolbar }}
          pageSize={25}
          rowsPerPageOptions={[25, 50, 100]}
          getRowId={(r) => r.id}
        />
      </Box>
    </Box>
  );
};

export default StreamRatings;
